'use client';
import React from 'react';
import { createSession, Session } from '@benzinga/session';

export type PollyText = `<speak>${string}</speak>`;

interface SessionProps {
  session?: Session | undefined;
}

export interface SessionPropsExtension extends SessionProps {
  getManager: Session['getManager'];
  getSession: () => Session;
}

export const SessionContext = React.createContext<SessionPropsExtension>({
  getManager: function (manager) {
    return this.getSession().getManager(manager);
  },
  getSession: function (): Session {
    if (this.session === undefined) {
      this.session = createSession();
    }
    return this.session;
  },
  session: undefined,
});

export type SessionContextType = React.ContextType<typeof SessionContext>;

export const SessionContextProvider: React.FC<React.PropsWithChildren<Partial<SessionProps>>> = React.memo(props => {
  const value = React.useMemo<SessionPropsExtension>(() => {
    return {
      getManager: function (manager) {
        return this.getSession().getManager(manager);
      },
      getSession: function (): Session {
        if (this.session === undefined) {
          this.session = createSession();
        }
        return this.session;
      },
      session: props.session ?? createSession(),
    };
  }, [props.session]);

  return <SessionContext.Provider value={value}>{props.children}</SessionContext.Provider>;
});

SessionContextProvider.displayName = 'SessionContextProvider';
